import { useState, useEffect } from "react";
import { checkoutTrackingApi } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  Loader2,
  RefreshCw,
  TrendingUp,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { formatAdminBusinessEt } from "@/lib/admin-datetime";
import { cn } from "@/lib/utils";

const CheckoutTracking = () => {
  const [activeTab, setActiveTab] = useState("failed");

  // Failed Checkouts State
  const [failedCheckouts, setFailedCheckouts] = useState<any[]>([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [failedPage, setFailedPage] = useState(1);
  const [failedTotal, setFailedTotal] = useState(0);
  const [failedFilters, setFailedFilters] = useState({
    hours: 24,
    limit: 50,
    errorType: "",
  });

  // Funnel Analytics State
  const [funnelData, setFunnelData] = useState<any>(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelFilters, setFunnelFilters] = useState({
    startDate: "",
    endDate: "",
    programId: "",
    deliveryMode: "",
  });

  // Timeline State
  const [timelineData, setTimelineData] = useState<any>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);

  useEffect(() => {
    if (activeTab === "failed") {
      loadFailedCheckouts();
    } else if (activeTab === "funnel") {
      loadFunnelAnalytics();
    }
  }, [activeTab, failedPage, failedFilters]);

  const loadFailedCheckouts = async () => {
    setFailedLoading(true);
    try {
      const response = await checkoutTrackingApi.getFailedCheckouts({
        hours: failedFilters.hours,
        limit: failedFilters.limit,
        page: failedPage,
        errorType: failedFilters.errorType || undefined,
      });
      if (response.success && response.data) {
        setFailedCheckouts(response.data.failedCheckouts || []);
        setFailedTotal(response.data.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error("Failed to load failed checkouts:", error);
      toast.error(error?.message || "Failed to load failed checkouts");
    } finally {
      setFailedLoading(false);
    }
  };

  const loadFunnelAnalytics = async () => {
    setFunnelLoading(true);
    try {
      const response = await checkoutTrackingApi.getFunnelAnalytics({
        startDate: funnelFilters.startDate || undefined,
        endDate: funnelFilters.endDate || undefined,
        programId: funnelFilters.programId || undefined,
        deliveryMode: funnelFilters.deliveryMode || undefined,
      });
      if (response.success && response.data) {
        setFunnelData(response.data);
      }
    } catch (error: any) {
      console.error("Failed to load funnel analytics:", error);
      toast.error(error?.message || "Failed to load funnel analytics");
    } finally {
      setFunnelLoading(false);
    }
  };

  const loadTimeline = async (id: string) => {
    if (!id.trim()) {
      toast.error("Please enter a transaction ID");
      return;
    }
    setTimelineLoading(true);
    try {
      const response = await checkoutTrackingApi.getCheckoutTimeline(id);
      if (response.success && response.data) {
        setTimelineData(response.data);
        setTimelineDialogOpen(true);
      }
    } catch (error: any) {
      console.error("Failed to load timeline:", error);
      toast.error(error?.message || "Failed to load timeline");
    } finally {
      setTimelineLoading(false);
    }
  };

  const formatDate = (dateString: string) => formatAdminBusinessEt(dateString);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="container mx-auto p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Checkout Tracking
          </h1>
          <p className="text-muted-foreground">
            Monitor failed checkouts, analyze conversion funnels, and debug transactions
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="failed">
            <AlertTriangle className="mr-2 h-4 w-4" />
            Failed Checkouts
          </TabsTrigger>
          <TabsTrigger value="funnel">
            <TrendingUp className="mr-2 h-4 w-4" />
            Funnel Analytics
          </TabsTrigger>
          <TabsTrigger value="timeline">
            <Search className="mr-2 h-4 w-4" />
            Timeline Debugger
          </TabsTrigger>
        </TabsList>

        {/* Failed Checkouts Tab */}
        <TabsContent value="failed" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Failed Checkouts Monitor</CardTitle>
                  <CardDescription>
                    Track and monitor failed checkout attempts
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select
                    value={failedFilters.hours.toString()}
                    onValueChange={(value) =>
                      setFailedFilters({ ...failedFilters, hours: parseInt(value) })
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">Last 24h</SelectItem>
                      <SelectItem value="48">Last 48h</SelectItem>
                      <SelectItem value="72">Last 72h</SelectItem>
                      <SelectItem value="168">Last 7 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadFailedCheckouts} disabled={failedLoading}>
                    {failedLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex gap-2">
                <Input
                  placeholder="Filter by error type..."
                  value={failedFilters.errorType}
                  onChange={(e) =>
                    setFailedFilters({ ...failedFilters, errorType: e.target.value })
                  }
                  className="max-w-sm"
                />
                {failedFilters.errorType && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setFailedFilters({ ...failedFilters, errorType: "" })
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {failedLoading ? (
                <TableSkeleton columns={7} rows={5} />
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Student</TableHead>
                          <TableHead>Program</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Payment Type</TableHead>
                          <TableHead>Error Reason</TableHead>
                          <TableHead>Transaction ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {failedCheckouts.length > 0 ? (
                          failedCheckouts.map((checkout) => (
                            <TableRow key={checkout._id}>
                              <TableCell className="font-medium">
                                {formatDate(checkout.createdAt)}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium">
                                    {checkout.studentId?.fullName || "N/A"}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {checkout.studentId?.email}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium">
                                    {checkout.programName}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {checkout.deliveryMode}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>{formatCurrency(checkout.amount)}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{checkout.paymentType}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-xs">
                                  <p className="text-sm text-red-600 truncate">
                                    {truncateText(checkout.reason, 60)}
                                  </p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="link"
                                  className="h-auto p-0 text-xs"
                                  onClick={() => {
                                    setTransactionId(checkout.transactionId);
                                    loadTimeline(checkout.transactionId);
                                  }}
                                >
                                  {checkout.transactionId.slice(0, 8)}...
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} className="h-24 text-center">
                              No failed checkouts found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {failedTotal > 0 && (
                    <div className="flex items-center justify-between py-4">
                      <div className="text-sm text-muted-foreground">
                        Showing {((failedPage - 1) * failedFilters.limit) + 1} to{" "}
                        {Math.min(failedPage * failedFilters.limit, failedTotal)} of{" "}
                        {failedTotal} results
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setFailedPage((p) => Math.max(1, p - 1))}
                          disabled={failedPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFailedPage((p) =>
                              Math.min(
                                Math.ceil(failedTotal / failedFilters.limit),
                                p + 1
                              )
                            )
                          }
                          disabled={
                            failedPage >= Math.ceil(failedTotal / failedFilters.limit)
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Funnel Analytics Tab */}
        <TabsContent value="funnel" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Checkout Funnel Analytics</CardTitle>
                  <CardDescription>
                    Analyze conversion rates and identify bottlenecks
                  </CardDescription>
                </div>
                <Button onClick={loadFunnelAnalytics} disabled={funnelLoading}>
                  {funnelLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="grid gap-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={funnelFilters.startDate}
                    onChange={(e) =>
                      setFunnelFilters({ ...funnelFilters, startDate: e.target.value })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={funnelFilters.endDate}
                    onChange={(e) =>
                      setFunnelFilters({ ...funnelFilters, endDate: e.target.value })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Program ID (optional)</Label>
                  <Input
                    placeholder="Filter by program ID"
                    value={funnelFilters.programId}
                    onChange={(e) =>
                      setFunnelFilters({ ...funnelFilters, programId: e.target.value })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Delivery Mode (optional)</Label>
                  <Select
                    value={funnelFilters.deliveryMode || "all"}
                    onValueChange={(value) =>
                      setFunnelFilters({ ...funnelFilters, deliveryMode: value === "all" ? "" : value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All modes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All modes</SelectItem>
                      <SelectItem value="ONLINE">Online</SelectItem>
                      <SelectItem value="ONCAMPUS">On Campus</SelectItem>
                      <SelectItem value="ZOOM">Zoom</SelectItem>
                      <SelectItem value="HYBRID">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {funnelLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : funnelData ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Started</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {funnelData.funnel?.started || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Card Added</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {funnelData.funnel?.cardAdded || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Completed</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {funnelData.funnel?.completed || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Failed</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-red-600">
                          {funnelData.funnel?.failed || 0}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                          Started → Card
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {funnelData.conversionRates?.startedToCard?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                          Card → Completed
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {funnelData.conversionRates?.cardToCompleted?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                          Overall Conversion
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {funnelData.conversionRates?.overallConversion?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {funnelData.averageTimes && (
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-sm">Average Times</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-muted-foreground">
                              Time to Card
                            </div>
                            <div className="text-lg font-semibold">
                              {funnelData.averageTimes.timeToCardMinutes?.toFixed(2) || 0}{" "}
                              minutes
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-muted-foreground">
                              Time to Complete
                            </div>
                            <div className="text-lg font-semibold">
                              {funnelData.averageTimes.timeToCompleteMinutes?.toFixed(2) ||
                                0}{" "}
                              minutes
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {funnelData.failureReasons &&
                    Object.keys(funnelData.failureReasons).length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">Failure Reasons</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {Object.entries(funnelData.failureReasons).map(
                              ([reason, count]) => (
                                <div
                                  key={reason}
                                  className="flex justify-between items-center p-2 border rounded"
                                >
                                  <span className="text-sm truncate flex-1 mr-2">
                                    {truncateText(reason, 80)}
                                  </span>
                                  <Badge variant="destructive">{count as number}</Badge>
                                </div>
                              )
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No funnel data available. Apply filters and click Refresh.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Debugger Tab */}
        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Checkout Timeline Debugger</CardTitle>
              <CardDescription>
                Get complete event timeline for a specific transaction
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Enter Transaction ID, Square Payment ID, or Subscription ID"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      loadTimeline(transactionId);
                    }
                  }}
                />
                <Button
                  onClick={() => loadTimeline(transactionId)}
                  disabled={timelineLoading || !transactionId.trim()}
                >
                  {timelineLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="mr-2 h-4 w-4" />
                  )}
                  Search
                </Button>
              </div>

              {timelineData && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle>Transaction Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Student</div>
                        <div className="font-medium">
                          {timelineData.summary?.student?.fullName}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {timelineData.summary?.student?.email}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Status</div>
                        <Badge
                          className={cn(
                            timelineData.summary?.status === "COMPLETED"
                              ? "bg-green-100 text-green-800"
                              : timelineData.summary?.status === "FAILED"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          )}
                        >
                          {timelineData.summary?.status}
                        </Badge>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Amount</div>
                        <div className="font-medium">
                          {formatCurrency(timelineData.summary?.amount || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Program</div>
                        <div className="font-medium">
                          {timelineData.summary?.programName}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="text-sm font-medium mb-2">Event Timeline</div>
                      <div className="space-y-2">
                        {timelineData.timeline?.map((event: any, index: number) => (
                          <div
                            key={event._id}
                            className="flex items-start gap-4 p-3 border rounded"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline">{event.eventType}</Badge>
                                <Badge
                                  className={cn(
                                    event.status === "COMPLETED"
                                      ? "bg-green-100 text-green-800"
                                      : event.status === "FAILED"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-yellow-100 text-yellow-800"
                                  )}
                                >
                                  {event.status}
                                </Badge>
                                {event.amount && (
                                  <span className="text-sm font-medium">
                                    {formatCurrency(event.amount)}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatDate(event.createdAt)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Timeline Dialog */}
      <Dialog open={timelineDialogOpen} onOpenChange={setTimelineDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transaction Timeline</DialogTitle>
            <DialogDescription>
              Complete event timeline for transaction: {transactionId}
            </DialogDescription>
          </DialogHeader>
          {timelineData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Student</div>
                  <div className="font-medium">
                    {timelineData.summary?.student?.fullName}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {timelineData.summary?.student?.email}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Status</div>
                  <Badge
                    className={cn(
                      timelineData.summary?.status === "COMPLETED"
                        ? "bg-green-100 text-green-800"
                        : timelineData.summary?.status === "FAILED"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                    )}
                  >
                    {timelineData.summary?.status}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Amount</div>
                  <div className="font-medium">
                    {formatCurrency(timelineData.summary?.amount || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Program</div>
                  <div className="font-medium">
                    {timelineData.summary?.programName}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-2">
                  Timeline ({timelineData.totalEvents} events)
                </div>
                <div className="space-y-2">
                  {timelineData.timeline?.map((event: any, index: number) => (
                    <div
                      key={event._id}
                      className="flex items-start gap-4 p-3 border rounded"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{event.eventType}</Badge>
                          <Badge
                            className={cn(
                              event.status === "COMPLETED"
                                ? "bg-green-100 text-green-800"
                                : event.status === "FAILED"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            )}
                          >
                            {event.status}
                          </Badge>
                          {event.amount && (
                            <span className="text-sm font-medium">
                              {formatCurrency(event.amount)}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(event.createdAt)}
                        </div>
                        {event.metadata && (
                          <div className="mt-2 text-xs bg-muted p-2 rounded">
                            <pre className="whitespace-pre-wrap">
                              {JSON.stringify(event.metadata, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTimelineDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CheckoutTracking;
